import remove from 'lodash/remove';
import uniq from 'lodash/uniq';
import { arrayOf, object } from 'prop-types';
import React from 'react';

import getDisplayName from '../../utils/get-display-name';

function getIdFromKey(key) {
  return key.split('select-')[1];
}

export default function withSelection({
  keyName: keyNameFn = () => undefined,
}) {
  return ChildComponent => {
    // noinspection JSPotentiallyInvalidUsageOfThis
    class WrappedComponent extends React.Component {
      state = {
        selectedItems: [],
        filteredSelectedItems: [],
        lastData: this.props.data,
        filteredData: this.props.data,
      };

      componentDidMount() {
        this.keyName = keyNameFn(this.props) || 'id';
      }

      static getDerivedStateFromProps(props, state) {
        const { data } = props;
        if (state.lastData !== data) {
          return {
            selectedItems: [],
            lastData: data,
            filteredSelectedItems: [],
            filteredData: data,
          };
        }
        return null;
      }

      componentDidUpdate(prevProps, prevState) {
        if (
          this.state.filteredData !== prevState.filteredData ||
          this.state.selectedItems !== prevState.selectedItems
        ) {
          if (this.state.filteredData.length > 0) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState(({ selectedItems, filteredData }) => ({
              filteredSelectedItems: selectedItems.filter(id =>
                filteredData.find(item => String(item[this.keyName]) === id),
              ),
            }));
          } else {
            // Handles scenarios when filterData is not present or id key is not available such as AddProductForm Component
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState(({ selectedItems }) => ({
              filteredSelectedItems: selectedItems,
            }));
          }
        }
      }

      handleToggleSelection = key => {
        const id = getIdFromKey(key);
        const { selectedItems } = this.state;
        if (!selectedItems.includes(id)) {
          this.setState({
            selectedItems: selectedItems.concat([id]),
          });
        } else {
          this.setState({
            selectedItems: selectedItems.filter(item => item !== id),
          });
        }
      };

      handleToggleSelectionMultiple = (keys, isSelected) => {
        const ids = keys.map(getIdFromKey);

        this.setState(state => {
          const selectedItems = [...state.selectedItems];
          ids.forEach(id => {
            if (isSelected) {
              selectedItems.push(id);
            } else {
              remove(selectedItems, item => item === id);
            }
          });

          return {
            selectedItems: uniq(selectedItems),
          };
        });
      };

      handleStateChange = ({ sortedData }) => {
        if (this.state.filteredData.length !== sortedData.length) {
          this.setState({
            filteredData: sortedData.map(({ _original }) => _original),
          });
        }
      };

      handleToggleAll = () => {
        const { filteredData } = this.state;
        if (this.areAllItemsSelected()) {
          this.setState({ selectedItems: [] });
        } else {
          this.setState({
            selectedItems: filteredData.map(({ [this.keyName]: id }) =>
              String(id),
            ),
          });
        }
      };

      areAllItemsSelected = () => {
        const { filteredData, filteredSelectedItems } = this.state;
        return (
          !!filteredData.length &&
          filteredSelectedItems.length === filteredData.length
        );
      };

      isItemSelected = id => this.state.selectedItems.includes(String(id));

      render() {
        return (
          <ChildComponent
            {...this.props}
            selectedItems={this.state.filteredSelectedItems}
            onToggleSelection={this.handleToggleSelection}
            onToggleSelectionMultiple={this.handleToggleSelectionMultiple}
            onToggleAll={this.handleToggleAll}
            onIsSelected={this.isItemSelected}
            onStateChange={this.handleStateChange}
            areAllSelected={this.areAllItemsSelected()}
          />
        );
      }
    }

    WrappedComponent.propTypes = {
      data: arrayOf(object), // eslint-disable-line react/forbid-prop-types
    };
    WrappedComponent.defaultProps = {
      data: [],
    };
    WrappedComponent.displayName = `withSelection(${getDisplayName(
      ChildComponent,
    )})`;

    return WrappedComponent;
  };
}
